<template>
	<div>
		<header class="page-title">
			<h2>Edit Provider List</h2>
			<div class="ml-auto alert alert-danger btn btn-md mb-0" v-if="!canWeSave">
				<div class="alert-content">
					<p v-if="missingProviders.totalErrors > 0">You can NOT save until you import every provider in every connected article!</p>
				</div>
			</div>
			<div class="ml-auto">
				<button class="btn btn-md btn-primary" @click="showHelpModal">
					<i class="fas fa-question-circle"></i> Help
				</button>
				<a :class="{'disabled': !canWeSave}" @click.prevent="updateProviderList" class="btn btn-md btn-success"><i class="fas fa-save"></i> Save Changes</a>
			</div>
		</header>

		<div class="flex box">
			<div class="six-lg">
				<div class="card">
					<header class="card-header">
						<h4 class="card-title">General</h4>
					</header>
					<div class="card-body">
						<div class="form-group">
							<label for="name">List Name</label>
							<input :disabled="!canUserEdit" id="name" name="name" type="text" v-model="providerList.name">
							<p class="input-error" v-if="errors.errors.name">{{ errors.errors.name }}</p>
						</div>
						<div class="form-group">
							<label for="provider-type">Provider Type</label>
							<select :disabled="!canUserEdit" id="provider-type" v-model="providerList.provider_type_id">
								<option :selected="type.id === providerList.provider_type_id" :value="type.id" v-for="type in types">{{ type.name }}</option>
							</select>
						</div>
						<div class="form-group">
							<label for="description">Internal Description</label>
							<textarea :disabled="!canUserEdit" class="textarea-small" id="description" name="description" v-model="providerList.description"></textarea>
						</div>
						<div class="form-group">
							<label for="provider-type">Shortcode Preview</label>
							<code class="code-inline">[[post-object type="best-buy-table" list="{{ providerLists.id }}"]]</code>
						</div>
					</div>
				</div>
			</div>
			<div class="six-lg">
				<div class="card">
					<header class="card-header">
						<h4 class="card-title">Order of Providers</h4>
					</header>
					<div class="card-body">
						<table>
							<thead>
								<tr>
									<th></th>
									<th>Position</th>
									<th>Provider Name</th>
									<th>Actions</th>
								</tr>
							</thead>
							<draggable :disabled="!canUserEdit" :element="'tbody'" :list="providerList.rows" @end="drag=false" @start="drag=true" @update="onUpdate">
								<tr class="draggable" v-for="(row,index) in providerList.rows" :class="{'tr-warning' : isThisMissing(row.selectedProvider) === 2}">
									<td :class="{ 'error-class' : isThisMissing(row.selectedProvider) === 1}" class="text-center">
										<i class="fas fa-grip-lines"></i></td>
									<td>{{ row.no }}.</td>
									<td>
										<select :disabled="!canUserEdit" @change="checkConnectedArticles" v-model="row.selectedProvider">
											<option value="null">Select Provider</option>
											<option :value="providerOption.id" v-for="providerOption in providerOptions">{{ providerOption.name }}</option>
										</select>
										<p class="input-error" v-if="errors.errors[errorSelectProvider(index)]">{{ errors.errors[errorSelectProvider(index)] }}</p>
									</td>
									<td class="nowrap">
										<a v-if="canUserEdit" @click.prevent="removeOption(index)" class="btn btn-icon btn-danger" href="#"><i class="fas fa-times" title="Remove"></i></a>
									</td>
								</tr>
							</draggable>
						</table>
						<div class="mt-2 text-right">
							<a v-if="canUserEdit" @click.prevent="addOption" class="btn btn-md btn-success" href="#"><i class="fas fa-plus"></i> Add New Provider</a>
						</div>
					</div>
				</div>
			</div>
			<div class="twelve-lg">
				<div class="card">
					<header class="card-header">
						<h4 class="card-title">Connected Articles</h4>
					</header>
					<div class="card-body">
						<table>
							<thead>
								<tr>
									<th class="text-center">ID</th>
									<th>Title</th>
									<th class="text-center">A/B</th>
									<th>Missing Providers</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tr v-for="article in missingProviders.articles" :class="{'tr-warning' : article.missingProviders.length > 0 && article.summary_section === 0}">
								<td :class="{'error-class' : (article.missingProviders.length > 0  && article.summary_section > 0) || article.ab_test}" class="text-center">{{ article.id }}</td>
								<td>{{ article.title }}</td>
								<td v-if="article.ab_test" class="error-class text-center">
									<i class="fas fa-times" title="Active AB Test"></i></td>
								<td v-if="!article.ab_test"></td>
								<td>{{ article.missingProviders.join(', ') }}</td>
								<td class="nowrap">
									<a :href="article.editUrl" class="btn btn-icon btn-primary" target="_blank"><i class="fas fa-fw fa-pencil-alt" title="Edit"></i></a>
									<a :disabled="article.missingProviderIds.length === 0" @click="massImportProviderDetails(article.id, article.missingProviderIds)" class="demo-click btn btn-icon btn-primary" title="Import All" v-if="userRoles.includes('articles.update')"><i class="fas fa-fw fa-file-import"></i></a>
									<a :href="article.url" class="btn btn-icon btn-default" target="_blank"><i class="fas fa-eye" title="View"></i></a>
								</td>
							</tr>
						</table>
					</div>
				</div>
			</div>
		</div>

		<modal @close="closeHelpModal" v-show="isHelpModalVisible">
			<template slot="header">
				<h4 class="modal-title">Help</h4>
			</template>
			<template slot="body">
				<h4>There are 3 statuses</h4>
				<p>No marks -> everything is fine</p>
				<p>Yellow -> There are no summary sections but unique data might be missing. WCS: there is a steroid list with a default slogan. This won't cause any issues, you can save the list.</p>
				<p>Red -> There's summary section and unique data is missing from it. WCS: There's a whole wall of text, at least 3 paragraphs, that's definitely harmful repeated content.  You can't save the list until fixed</p>
			</template>
			<template slot="footer">
				<a @click="closeHelpModal" aria-label="Close modal" class="btn btn-md btn-primary modal-close" href="#"><i class="fas fa-times"></i> Close</a>
			</template>
		</modal>
	</div>
</template>

<script>
import modal from '../Modal/DefaultModal.vue';
import draggable from 'vuedraggable';
import Errors from '../../Utils/Errors.js';

export default {
	components: {
		modal,
		draggable,
	},
	props: {
		dataProviderList: {
			type: Object
		},
		dataTypes: {
			type: Object
		},
		dataProviders: {
			type: Array
		}
	},
	created: function () {
		this.populateProviderList();
		this.checkConnectedArticles();
	},
	data() {
		return {
			userRoles: this.$currentUserRoles,
			providerLists: this.dataProviderList,
			types: this.dataTypes,
			providers: this.dataProviders,
			providerList: {
				name: this.dataProviderList.name,
				provider_type_id: this.dataProviderList.provider_type_id,
				description: this.dataProviderList.description,
				rows: [],
			},
			missingProviders: {
				missingProviders: [],
				articles: [],
			},
			canWeSave: true,
			inArticles: [],
			articles: [],

			isModalVisible: false,
			isHelpModalVisible: false,

			errors: new Errors()
		}
	},
	computed: {
		canUserEdit: function () {
			if (this.userRoles.length !== 0) {
				return this.userRoles.includes('provider_lists_tables.update');
			}
			return false;
		},
		providerOptions: function () {
			let populateProviderOptions = [];
			for (let i = 0; i < this.providers.length; i++) {
				if (this.providers[i].provider_type_id === this.providerList.provider_type_id) {
					populateProviderOptions.push(this.providers[i]);
				} // if
			} // for
			return populateProviderOptions;
		}
	},
	methods: {
		closeHelpModal: function () {
			this.isHelpModalVisible = false;
		},
		showHelpModal: function () {
			this.isHelpModalVisible = true;
		},
		populateProviderList: function () {
			let options = [];
			this.providerLists.providers.forEach(function (part, key) {
				options.push({
					no: key + 1,
					selectedProvider: part.id
				});
			});
			this.providerList.rows = options;
		},
		addOption: function () {
			this.providerList.rows.push(
					{
						no: this.providerList.rows.length + 1,
						selectedProvider: null
					});
			this.checkConnectedArticles();
		},
		removeOption: function (index) {
			this.providerList.rows.splice(index, 1);
			this.updateRowsOrder();
			this.checkConnectedArticles();
			this.providerLists.providers.splice(index, 1);
		},
		onUpdate: function () {
			this.updateRowsOrder();
		},
		updateRowsOrder: function () {
			// reset the order to be 1,2,3 always
			let i = 1;
			this.providerList.rows.forEach(function (part, key) {
				this[key]["no"] = i;
				i++;
			}, this.providerList.rows);
		},
		updateProviderList: function () {
			let vm = this;

			if (vm.canWeSave !== true) {
				return true;
			}

			let currentDateWithFormat = new Date().toJSON().slice(0, 10).split('-').reverse().join('/')
			this.$dialog.confirm(
					"Do you want to update the displayed date on this " + this.inArticles.length + " articles to " + currentDateWithFormat + "?",
					{
						okText: 'Yes',
						cancelText: 'No',
					})
					.then(function () {
						vm.changeDisplayDate(vm.articles);
					})
					.catch(function () {
						vm.changeDisplayDate([]);
					});
		},
		changeDisplayDate: function (data) {
			this.providerList.articles = data;
			axios.put('/api/' + this.$selectedSite.slug + '/provider-list/' + this.providerLists.id, this.providerList)
					.then(response => {
						if (response.data.success) {
							this.$toastr.success('Provider List saved successfully', 'Success');
							this.checkConnectedArticles();
						} else {
							this.$toastr.error(response.data.failed, 'Error');
						} // if
					})
					.catch(e => {
						this.errors.record(e.response.data);
						this.$toastr.error('Provider List save failed.', 'Error');
					});
		},
		errorSelectProvider: function (index) {
			return 'rows_' + index + '_selectedProvider';
		},
		checkConnectedArticles: function () {
			let vm = this;
			axios.post('/api/' + this.$selectedSite.slug + '/providerlist/connectedarticles/' + this.providerLists.id, {
				providers: vm.providerList.rows
			})
					.then(response => {
						if (response.status === 200) {
							this.$toastr.success('Connected Articles', 'Success');
							this.inArticles = response.data.articles;

							let getArticles = [];
							response.data.articles.forEach(function (article, key) {
								getArticles.push(article.id);
							});
							this.articles = getArticles;

							vm.missingProviders = response.data;
							vm.canWeSave = response.data.hasOwnProperty('totalErrors') && response.data.totalErrors === 0;
						}
					})
					.catch(e => {
						this.errors.record(e.response.data);
						this.$toastr.error('Provider List save failed.', 'Error');
					});
		},
		isThisMissing: function (providerId) {
			if (!this.canWeSave && this.missingProviders.missingProviders.includes(providerId)) {
				return 1;
			} // if
			if(this.canWeSave && this.missingProviders.missingProviders.includes(providerId)) {
				return 2;
			} // if
			return false;
		},
		massImportProviderDetails(articleId, providerIds) {
			let vm = this;

			this.$dialog.confirm('Are you sure you want to copy this provider details?', {
				loader: true
			})
					.then((dialog) => {
						axios.post('/api/' + vm.$selectedSite.slug + '/massImportProviderDetails/' + articleId, {
							providerIds: providerIds
						})
								.then(response => {
									vm.providerAdditional = response.data.details;
									dialog.close();
									vm.isModalVisible = true;
									vm.checkConnectedArticles();
								})
								.catch(e => {
									vm.errors.record(e.data);
									dialog.close();
									this.$toastr.error('Import provider details failed.', 'Error');
								});
					})
					.catch(() => {
						this.$toastr.info('Provider import cancelled.', 'Info');
					});
		}
	}
}
</script>

<style scoped>


</style>
